import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import './Dashboard.css';
import { useDesignTheme } from '../../context/DesignThemeContext';
import DocumentsReport from '../Reports/DocumentsReport';
import WorkReports from '../Reports/WorkReports';
import ExcelUpload from '../Reports/ExcelUpload';
import TenantsReport from '../Reports/TenantsReport';
import ContractScreen from '../Reports/ContractScreen';
import PaymentsReport from '../Reports/PaymentsReport';
import ContactPage from '../Contact/ContactPage';
import SettingsPage from './SettingsPage';
import TableActionsMenu from '../Common/TableActionsMenu';
import ColumnVisibilityMenu from '../Common/ColumnVisibilityMenu';
import SendMessages from '../Messages/SendMessages';
import SentMessagesReport from '../Messages/SentMessagesReport';
import { isMessagingUser, isTenantUser } from '../Messages/messageUtils';

const getContractId = (contract) => String(contract?.DocNum ?? contract?.id ?? '');

const getContractDisplayLabel = (contract) => {
  const name = contract?.U_Project || contract?.name || '';
  const docNum = getContractId(contract);
  if (name && docNum) return `${name} (${docNum})`;
  return name || docNum;
};

const MENU_BG_URL = `${process.env.PUBLIC_URL}/img/mnuTrblsi.svg`;
const CERAMIC_LOGO_URL = `${process.env.PUBLIC_URL}/img/trabelci-ceramic-logo.png`;
const BOQ_UNSAVED_MESSAGE = 'יש שינויים בכתב כמויות שלא נשמרו. הנתונים לא יישמרו. האם ברצונך להמשיך?';
const TENANT_RESTRICTED_TABS = ['tenants', 'contract', 'excel-upload'];
const TENANT_BLOCKED_DASHBOARD_LINKS = new Set([2, 3, 7, '2', '3', '7']);

const Dashboard = ({ user, onLogout, sidebarOpen, onSidebarOpenChange }) => {
  const { topNavLayout, isCeramicTheme } = useDesignTheme();
  const [activeTab, setActiveTab] = useState('dashboard');
  const setSidebarOpen = onSidebarOpenChange;
  const contracts = user?.contracts || [];
  const boqUnsavedRef = useRef(false);

  const [selectedContractId, setSelectedContractId] = useState(() => {
    if (contracts.length > 0) return getContractId(contracts[0]);
    return '';
  });

  useEffect(() => {
    if (contracts.length === 0) return;
    setSelectedContractId((prev) => {
      if (prev && contracts.some((c) => getContractId(c) === prev)) return prev;
      return getContractId(contracts[0]);
    });
  }, [contracts]);

  const selectedContractName = useMemo(() => {
    const match = contracts.find((c) => getContractId(c) === selectedContractId);
    return match ? (match.U_Project || match.name || '') : '';
  }, [contracts, selectedContractId]);

  const [freeSearch, setFreeSearch] = useState('');
  const [boqSummary, setBoqSummary] = useState(null);
  const [documentsColumnState, setDocumentsColumnState] = useState(null);
  const [documentsToolbarActions, setDocumentsToolbarActions] = useState(null);
  const [documentsPrefilter, setDocumentsPrefilter] = useState(null);
  const tableActionsRef = useRef(null);
  const reportSearchRef = useRef(null);
  const tableTabs = ['documents', 'tenants', 'payments', 'work-reports', 'sent-messages'];
  const showFreeSearch = tableTabs.includes(activeTab);
  const isMessagingAdmin = isMessagingUser(user?.type);
  const isTenant = isTenantUser(user?.type);
  const isReportTab = activeTab !== 'dashboard' && activeTab !== 'contact' && activeTab !== 'settings';
  const isScrollablePage = !isReportTab;
  const showProjectToolbar = isReportTab || activeTab === 'send-messages';

  useEffect(() => {
    const syncSidebarForViewport = () => {
      if (window.matchMedia('(max-width: 768px)').matches) {
        setSidebarOpen(false);
      }
    };

    syncSidebarForViewport();
    window.addEventListener('resize', syncSidebarForViewport);
    return () => window.removeEventListener('resize', syncSidebarForViewport);
  }, [setSidebarOpen]);

  useEffect(() => {
    setFreeSearch('');
  }, [activeTab]);

  useEffect(() => {
    if (!isMessagingAdmin && (activeTab === 'send-messages' || activeTab === 'sent-messages')) {
      setActiveTab('dashboard');
    }
  }, [isMessagingAdmin, activeTab]);

  useEffect(() => {
    if (isTenant && TENANT_RESTRICTED_TABS.includes(activeTab)) {
      setActiveTab('dashboard');
    }
  }, [isTenant, activeTab]);

  const handleExportExcel = () => tableActionsRef.current?.exportToExcel?.();
  const handlePrintTable = () => tableActionsRef.current?.printTable?.();

  const handleNavigateToDocuments = useCallback((filter) => {
    setDocumentsPrefilter(filter);
    setActiveTab('documents');
  }, []);

  useEffect(() => {
    if (activeTab !== 'excel-upload') {
      setBoqSummary(null);
    }
    if (activeTab !== 'documents') {
      setDocumentsColumnState(null);
      setDocumentsToolbarActions(null);
      setDocumentsPrefilter(null);
    }
  }, [activeTab]);

  const reportProps = { user, selectedContractId, search: freeSearch, tableActionsRef };

  const baseMenuItems = [
    { id: 'dashboard', label: 'דשבורד', icon: 'dashboard' },
    { id: 'documents', label: 'מסמכים', icon: 'documents' },
    ...(isTenant ? [] : [
      { id: 'tenants', label: 'דיירים', icon: 'tenants' },
      { id: 'contract', label: 'חוזה', icon: 'contract' },
    ]),
    { id: 'payments', label: 'תשלומים', icon: 'payments' },
    ...(isTenant ? [] : [{ id: 'excel-upload', label: 'כתב כמויות', icon: 'upload' }]),
  ];

  const messagingMenuItems = isMessagingAdmin
    ? [{ id: 'send-messages', label: 'הודעות', icon: 'messages' }]
    : [];

  const menuItems = [
    ...baseMenuItems,
    ...messagingMenuItems,
    { id: 'contact', label: 'צור קשר', icon: 'contact' },
    { id: 'settings', label: 'הגדרות', icon: 'settings' },
  ];

  const handleTabChange = (tabId) => {
    if (
      activeTab === 'excel-upload'
      && tabId !== 'excel-upload'
      && boqUnsavedRef.current
      && !window.confirm(BOQ_UNSAVED_MESSAGE)
    ) {
      return;
    }

    setActiveTab(tabId);
    if (window.matchMedia('(max-width: 768px)').matches) {
      setSidebarOpen(false);
    }
  };

  const handleContractChange = (newContractId) => {
    if (
      activeTab === 'excel-upload'
      && boqUnsavedRef.current
      && newContractId !== selectedContractId
      && !window.confirm(BOQ_UNSAVED_MESSAGE)
    ) {
      return;
    }

    setSelectedContractId(newContractId);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardHome dashboard={user?.dashboard} onTabChange={handleTabChange} isTenant={isTenant} />;
      case 'documents':
        return (
          <DocumentsReport
            {...reportProps}
            onColumnStateChange={setDocumentsColumnState}
            onToolbarActionsChange={setDocumentsToolbarActions}
            documentsPrefilter={documentsPrefilter}
            onDocumentsPrefilterApplied={() => setDocumentsPrefilter(null)}
          />
        );
      case 'tenants':
        return (
          <TenantsReport
            {...reportProps}
            onNavigateToDocuments={handleNavigateToDocuments}
          />
        );
      case 'contract':
        return <ContractScreen {...reportProps} />;
      case 'payments':
        return <PaymentsReport {...reportProps} />;
      case 'work-reports':
        return <WorkReports {...reportProps} />;
      case 'excel-upload':
        return <ExcelUpload {...reportProps} unsavedChangesRef={boqUnsavedRef} onSummaryChange={setBoqSummary} />;
      case 'send-messages':
        return (
          <SendMessages
            user={user}
            selectedContractId={selectedContractId}
            projectName={selectedContractName}
            onOpenSentHistory={() => handleTabChange('sent-messages')}
          />
        );
      case 'sent-messages':
        return <SentMessagesReport {...reportProps} />;
      case 'contact':
        return <ContactPage />;
      case 'settings':
        return <SettingsPage />;
      default:
        return <DashboardHome dashboard={user?.dashboard} onTabChange={handleTabChange} isTenant={isTenant} />;
    }
  };

  const renderNavButton = (item, className, onNavigate) => (
    <button
      key={item.id}
      type="button"
      className={`${className} ${activeTab === item.id ? 'active' : ''}`}
      onClick={() => onNavigate(item.id)}
      title={item.label}
      aria-label={item.label}
    >
      <SidebarIcon name={item.icon} />
      <span className="sidebar-nav-label">{item.label}</span>
    </button>
  );

  return (
    <div className={`dashboard-container${!topNavLayout && sidebarOpen ? ' dashboard-container--sidebar-open' : ''}`}>
      {!topNavLayout && (
        <button
          type="button"
          className={`sidebar-overlay${sidebarOpen ? ' sidebar-overlay--visible' : ''}`}
          aria-label="סגירת תפריט"
          aria-hidden={!sidebarOpen}
          tabIndex={sidebarOpen ? 0 : -1}
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <header className="dashboard-header">
        <div className="header-start">
          {!topNavLayout && (
            <button
              type="button"
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'סגירת תפריט' : 'פתיחת תפריט'}
              aria-expanded={sidebarOpen}
            >
              <span /><span /><span />
            </button>
          )}
          {isCeramicTheme && (
            <img
              src={CERAMIC_LOGO_URL}
              alt="Trabelci Ceramic"
              className="header-brand-logo"
            />
          )}
          <div className="header-greeting">
            שלום{user?.username ? `, ${user.username}` : ''}
          </div>
        </div>

        <div className="header-end">
          <button type="button" onClick={onLogout} className="logout-button">
            התנתק
          </button>
        </div>
      </header>

      {topNavLayout && (
        <nav className="dashboard-topnav" aria-label="ניווט ראשי">
          <div className="dashboard-topnav-inner">
            {menuItems.map((item) => renderNavButton(item, 'dashboard-topnav-item', handleTabChange))}
          </div>
        </nav>
      )}

      {!topNavLayout && (
      <aside
        className={`dashboard-sidebar ${sidebarOpen ? 'dashboard-sidebar--open' : ''}`}
      >
        <div className="sidebar-bg" aria-hidden="true">
          <img src={MENU_BG_URL} alt="" />
        </div>
        <div className="sidebar-inner">
          <div className="sidebar-top">
            <span className="sidebar-mobile-title">תפריט</span>
            <button
              type="button"
              className="sidebar-close"
              onClick={() => setSidebarOpen(false)}
              aria-label="סגירת תפריט"
              title="סגירת תפריט"
            >
              <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
          <nav className="sidebar-nav">
            {menuItems.map((item) => renderNavButton(item, 'sidebar-nav-item', handleTabChange))}
          </nav>
        </div>
      </aside>
      )}

      <div className="dashboard-main">
        {contracts.length > 0 && showProjectToolbar && (
          <div className="report-toolbar">
            <div className="report-toolbar-project">
              <span className="report-toolbar-project-label">פרויקט:</span>
              {contracts.length > 1 ? (
                <select
                  className="report-toolbar-project-select"
                  value={selectedContractId}
                  onChange={(e) => handleContractChange(e.target.value)}
                >
                  {contracts.map((c) => (
                    <option key={getContractId(c)} value={getContractId(c)}>
                      {getContractDisplayLabel(c)}
                    </option>
                  ))}
                </select>
              ) : (
                <span className="report-toolbar-project-name">
                  {getContractDisplayLabel(contracts.find((c) => getContractId(c) === selectedContractId))}
                </span>
              )}
              {activeTab === 'excel-upload' && boqSummary && (
                <div className="report-toolbar-boq-meta">
                  <div className="report-toolbar-boq-summary">
                    <span>בניינים: {boqSummary.buildings}</span>
                    <span>דיירים: {boqSummary.tenants}</span>
                  </div>
                  {boqSummary.statusLabel && (
                    <span className={`boq-status-badge boq-status-badge--${boqSummary.statusVariant || 'default'}`}>
                      {boqSummary.statusLabel}
                    </span>
                  )}
                </div>
              )}
            </div>

            {showFreeSearch && (
              <div className="report-toolbar-end">
                <div className="report-search-wrap">
                  <input
                    ref={reportSearchRef}
                    type="search"
                    className="report-search"
                    placeholder="...חפש"
                    value={freeSearch}
                    onChange={(e) => setFreeSearch(e.target.value)}
                  />
                  <SearchIcon />
                </div>
                {activeTab === 'documents' && documentsColumnState && (
                  <ColumnVisibilityMenu
                    columns={documentsColumnState.columns}
                    hiddenIds={documentsColumnState.hiddenIds}
                    onToggle={documentsColumnState.onToggle}
                    onShowAll={documentsColumnState.onShowAll}
                  />
                )}
                {activeTab === 'documents' && documentsToolbarActions && (
                  <button
                    type="button"
                    className="report-action-btn report-action-btn--email report-action-btn--icon"
                    onClick={documentsToolbarActions.onSendEmail}
                    data-tooltip="שליחה במייל לעדכון"
                    aria-label="שליחה במייל לעדכון"
                  >
                    <EmailUpdateIcon />
                  </button>
                )}
                <TableActionsMenu
                  variant="report"
                  onExport={handleExportExcel}
                  onPrint={handlePrintTable}
                />
              </div>
            )}
          </div>
        )}

        <main className={`dashboard-content${isReportTab ? ' dashboard-content--report' : ''}`}>
          <div className={`dashboard-content-body${isScrollablePage ? ' dashboard-content-body--scroll' : ''}`}>
            {renderContent()}
          </div>
        </main>
      </div>

      {!topNavLayout && !sidebarOpen && (
        <button
          type="button"
          className="sidebar-open-tab"
          onClick={() => setSidebarOpen(true)}
          aria-label="פתיחת תפריט"
          title="פתיחת תפריט"
        >
          <span aria-hidden="true">☰</span>
        </button>
      )}
    </div>
  );
};

const SearchIcon = ({ className = 'report-search-icon' }) => (
  <span className={className} aria-hidden="true">
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="M15.5 15.5L20 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  </span>
);

const EmailUpdateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" strokeLinejoin="round" />
    <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SidebarIcon = ({ name }) => {
  const props = {
    className: 'sidebar-nav-svg',
    viewBox: '0 0 24 24',
    fill: 'none',
    xmlns: 'http://www.w3.org/2000/svg',
    'aria-hidden': true,
  };

  const icons = {
    dashboard: (
      <svg {...props}>
        <path d="M4 10.5L12 4l8 6.5V19a1 1 0 01-1 1h-5v-6H10v6H5a1 1 0 01-1-1v-8.5z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
    documents: (
      <svg {...props}>
        <path d="M8 4h8l4 4v12a1 1 0 01-1 1H7a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M16 4v4h4M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    tenants: (
      <svg {...props}>
        <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
        <circle cx="17" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.6" />
        <path d="M4 19c0-3 2.5-5 5-5s5 2 5 5M14 19c0-2 1.5-3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    contract: (
      <svg {...props}>
        <path d="M7 3h10l4 4v14H7V3z" stroke="currentColor" strokeWidth="1.6" />
        <path d="M17 3v4h4M9 12h6M9 16h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    payments: (
      <svg {...props}>
        <rect x="3" y="6" width="18" height="13" rx="2" stroke="currentColor" strokeWidth="1.6" />
        <path d="M3 10h18M7 15h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    upload: (
      <svg {...props}>
        <path d="M12 16V6M8 10l4-4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 18h16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    contact: (
      <svg {...props}>
        <path d="M6 5h12a2 2 0 012 2v10l-4-3H8L4 17V7a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      </svg>
    ),
    settings: (
      <svg {...props}>
        <path
          d="M12 15.5a3.5 3.5 0 100-7 3.5 3.5 0 000 7z"
          stroke="currentColor"
          strokeWidth="1.6"
        />
        <path
          d="M19.4 13a7.8 7.8 0 000-2l2-1.2-2-3.5-2.3.7a7.9 7.9 0 00-1.7-1L15 3h-4l-.4 2.5a7.9 7.9 0 00-1.7 1L6.6 5.3l-2 3.5 2 1.2a7.8 7.8 0 000 2l-2 1.2 2 3.5 2.3-.7a7.9 7.9 0 001.7 1L11 21h4l.4-2.5a7.9 7.9 0 001.7-1l2.3.7 2-3.5-2-1.2z"
          stroke="currentColor"
          strokeWidth="1.3"
          strokeLinejoin="round"
        />
      </svg>
    ),
    messages: (
      <svg {...props}>
        <path d="M21 12a8 8 0 01-8 8H7l-4 3V8a8 8 0 018-8h6a8 8 0 018 4z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    'sent-messages': (
      <svg {...props}>
        <path d="M9 5h10a2 2 0 012 2v10H7l-4 3V7a2 2 0 012-2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
        <path d="M12 11l2 2 4-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  };

  return icons[name] || null;
};

const DashboardHome = ({ dashboard, onTabChange, isTenant = false }) => {
  const [cardSearch, setCardSearch] = useState('');

  if (!dashboard || !Array.isArray(dashboard)) {
    return (
      <div className="dashboard-home">
        <div className="dashboard-home-top">
          <h2 className="dashboard-home-title">ברוכים הבאים לפורטל הניהול של טרבלסי</h2>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>טוען...</div>
      </div>
    );
  }

  const linkToTabMap = {
    1: 'documents',
    2: 'tenants',
    3: 'contract',
    4: 'payments',
    7: 'excel-upload',
  };

  const filteredDashboard = dashboard.filter((item) => {
    if (!cardSearch.trim()) return true;
    const q = cardSearch.trim().toLowerCase();
    const title = (item.U_Title || item.Title || '').toLowerCase();
    const subtitle = (item.U_Subtitle || item.Subtitle || '').toLowerCase();
    return title.includes(q) || subtitle.includes(q);
  });

  const featuredIndex = dashboard.findIndex((item) => {
    const title = item.U_Title || item.Title || '';
    return title.includes('סך') || title.includes('סה"כ') || title.includes('תשלום');
  });

  const isDashboardLinkBlocked = (linkTo) => isTenant && TENANT_BLOCKED_DASHBOARD_LINKS.has(linkTo);

  const handleCardClick = (linkTo) => {
    if (isDashboardLinkBlocked(linkTo)) return;

    if (linkTo && linkTo !== -1 && linkTo !== '-1' && linkTo !== '') {
      const tabId = linkToTabMap[linkTo];
      if (tabId && onTabChange) {
        onTabChange(tabId);
      }
    }
  };

  const getTextColor = (color) => {
    if (!color || color === '' || color === 'black' || color === 'Black' || color === 'BLACK') {
      return null;
    }
    return color;
  };

  return (
    <div className="dashboard-home">
      <div className="dashboard-home-top">
        <h2 className="dashboard-home-title">ברוכים הבאים לפורטל הניהול של טרבלסי</h2>
        <div className="dashboard-home-search-wrap">
          <input
            type="search"
            className="dashboard-home-search"
            placeholder="...חפש"
            value={cardSearch}
            onChange={(e) => setCardSearch(e.target.value)}
          />
          <SearchIcon className="dashboard-home-search-icon" />
        </div>
      </div>

      <div className="stats-grid">
        {filteredDashboard.map((item) => {
          const originalIndex = dashboard.indexOf(item);
          let value = item.U_QryResult !== undefined ? item.U_QryResult : (item.U_Value || item.Value || 0);
          const title = item.U_Title || item.Title || '';
          const subtitle = item.U_Subtitle || item.Subtitle || '';
          const linkTo = item.U_LinkTo;
          const color = item.U_Color;

          const isClickable = linkTo && linkTo !== -1 && linkTo !== '-1' && linkTo !== ''
            && !isDashboardLinkBlocked(linkTo);
          const isFeatured = originalIndex === featuredIndex;

          if (value && typeof value === 'object') {
            value = JSON.stringify(value);
          }

          if (value === null || value === undefined) {
            value = 0;
          }

          const formattedValue = typeof value === 'number'
            ? (title.includes('תשלום') || title.includes('רווח') || title.includes('סכום') || title.includes('סך')
                ? `₪ ${value.toLocaleString()}`
                : value.toLocaleString())
            : String(value);

          const textColor = getTextColor(color);
          const textStyle = textColor ? { color: textColor } : {};

          return (
            <div
              key={originalIndex}
              className={[
                'stat-card',
                isFeatured ? 'stat-card--featured' : '',
                isClickable ? 'stat-card--clickable' : '',
              ].filter(Boolean).join(' ')}
              style={{
                cursor: isClickable ? 'pointer' : 'default',
              }}
              onClick={() => handleCardClick(linkTo)}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={(e) => {
                if (isClickable && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  handleCardClick(linkTo);
                }
              }}
            >
              <div className="stat-card-top">
                <span className="stat-card-icon" aria-hidden="true">
                  <StatCardIcon linkTo={linkTo} />
                </span>
                <h3 className="stat-card-title" style={textStyle}>{title}</h3>
              </div>
              <p className="stat-number" style={textStyle}>{formattedValue}</p>
              {subtitle ? <p className="stat-label" style={textStyle}>{subtitle}</p> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const StatCardIcon = ({ linkTo }) => {
  const iconProps = { viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: '1.8' };

  const icons = {
    1: <svg {...iconProps}><path d="M8 4h8l4 4v12H7V5a1 1 0 011-1z" /><path d="M16 4v4h4" /></svg>,
    2: <svg {...iconProps}><circle cx="9" cy="8" r="3" /><path d="M4 19c0-3 2.5-5 5-5s5 2 5 5" /></svg>,
    3: <svg {...iconProps}><path d="M7 3h10l4 4v14H7V3z" /><path d="M17 3v4h4" /></svg>,
    4: <svg {...iconProps}><rect x="3" y="6" width="18" height="13" rx="2" /><path d="M3 10h18" /></svg>,
    7: <svg {...iconProps}><path d="M12 16V6M8 10l4-4 4 4" /><path d="M4 18h16" /></svg>,
  };

  return icons[linkTo] || (
    <svg {...iconProps}>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M8 12h8" />
    </svg>
  );
};

export default Dashboard;
